import { Box, Chip, IconButton, List, ListItemButton, ListItemIcon, ListItemText, Paper, Typography, useMediaQuery, useTheme } from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { useMemo } from "react";
import { Link, Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../../../auth/AuthContext";
import { hasPermission } from "../../../auth/rolePermissions";
import { SETTINGS_ROUTE, SettingsGroup, SettingsSection, settingsGroups } from "../settingsSections";

/** Groups with only the sections this user may open ("soon" ones are shown, disabled). */
const useVisibleGroups = (): SettingsGroup[] => {
  const { user } = useAuth();
  return useMemo(
    () =>
      settingsGroups
        .map((group) => ({ ...group, sections: group.sections.filter((section) => hasPermission(user?.role, section.permission)) }))
        .filter((group) => group.sections.length > 0),
    [user]
  );
};

const SectionNav = ({ groups, activeKey, wide }: { groups: SettingsGroup[]; activeKey?: string; wide: boolean }) => (
  <Paper
    elevation={0}
    component="nav"
    aria-label="Settings sections"
    sx={{ border: "1px solid #e5e7eb", borderRadius: 3, boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)", p: 1 }}
  >
    {groups.map((group) => (
      <Box key={group.label} sx={{ "& + &": { mt: 0.5 } }}>
        <Typography sx={{ px: 1.5, pt: 1.25, pb: 0.5, fontSize: 11.5, fontWeight: 700, letterSpacing: "0.04em", color: "#94a3b8", textTransform: "uppercase" }}>
          {group.label}
        </Typography>
        <List disablePadding>
          {group.sections.map((section) => (
            <SectionNavItem key={section.key} section={section} active={section.key === activeKey} wide={wide} />
          ))}
        </List>
      </Box>
    ))}
  </Paper>
);

const SectionNavItem = ({ section, active, wide }: { section: SettingsSection; active: boolean; wide: boolean }) => {
  const Icon = section.icon;
  const soon = section.status === "soon";
  return (
    <ListItemButton
      component={soon ? "div" : (Link as React.ElementType)}
      to={soon ? undefined : `${SETTINGS_ROUTE}/${section.key}`}
      disabled={soon}
      selected={active}
      sx={{
        borderRadius: 2,
        py: wide ? 0.9 : 1.25,
        mb: 0.25,
        "&.Mui-disabled": { opacity: 1, color: "#94a3b8" },
        "&.Mui-selected, &.Mui-selected:hover": { bgcolor: "rgba(25, 118, 210, 0.08)", color: "primary.main" },
      }}
    >
      <ListItemIcon sx={{ minWidth: 36 }}>
        <Box
          sx={{
            width: 26,
            height: 26,
            borderRadius: 1.5,
            display: "grid",
            placeItems: "center",
            bgcolor: active ? "primary.main" : "#f1f5f9",
            color: active ? "#fff" : soon ? "#cbd5e1" : "#64748b",
            "& svg": { fontSize: 16 },
          }}
        >
          <Icon />
        </Box>
      </ListItemIcon>
      <ListItemText
        primary={section.label}
        secondary={wide ? undefined : section.description}
        slotProps={{ primary: { fontSize: 14, fontWeight: active ? 600 : 500 }, secondary: { fontSize: 12.5 } }}
      />
      {soon && <Chip label="Soon" size="small" sx={{ height: 20, fontSize: 10.5, fontWeight: 600, bgcolor: "#f1f5f9", color: "#94a3b8" }} />}
      {!soon && !wide && <ChevronRightRoundedIcon sx={{ color: "#cbd5e1" }} />}
    </ListItemButton>
  );
};

/**
 * Settings: one page for the whole school. Side navigation on the left, the chosen section on the
 * right (each section is a child route). On phones the section list is the first screen and a
 * section opens on its own with a back arrow.
 */
export const SettingsPage = () => {
  const theme = useTheme();
  const wide = useMediaQuery(theme.breakpoints.up("md"));
  const { pathname } = useLocation();
  const groups = useVisibleGroups();

  const sections = groups.flatMap((group) => group.sections);
  const activeKey = pathname.startsWith(SETTINGS_ROUTE + "/") ? pathname.slice(SETTINGS_ROUTE.length + 1).split("/")[0] : undefined;
  const activeSection = sections.find((section) => section.key === activeKey);
  const firstSection = sections.find((section) => section.status === "active");

  // /app/settings: wide screens open the first section; phones show the list
  if (!activeKey && wide && firstSection) return <Navigate to={`${SETTINGS_ROUTE}/${firstSection.key}`} replace />;

  const header = (
    <Box sx={{ mb: { xs: 2, md: 3 }, display: "flex", alignItems: "center", gap: 1 }}>
      {!wide && activeSection && (
        <IconButton component={Link} to={SETTINGS_ROUTE} aria-label="All settings" edge="start">
          <ArrowBackRoundedIcon />
        </IconButton>
      )}
      <Box>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 800, fontSize: { xs: "1.35rem", sm: "1.6rem" }, color: "#0f172a", letterSpacing: "-0.01em" }}>
          {!wide && activeSection ? activeSection.label : "Settings"}
        </Typography>
        <Typography sx={{ color: "text.secondary", fontSize: 14, mt: 0.25 }}>
          {wide && activeSection ? `Settings › ${activeSection.label}` : !wide && activeSection ? activeSection.description : "Everything about how your school is set up."}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ width: "100%", maxWidth: 1180, mx: "auto", p: { xs: 2, sm: 3 } }}>
      {header}
      {wide ? (
        <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start" }}>
          <Box sx={{ width: 248, flexShrink: 0, position: "sticky", top: 88 }}>
            <SectionNav groups={groups} activeKey={activeKey} wide />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Outlet />
          </Box>
        </Box>
      ) : activeKey ? (
        <Outlet />
      ) : (
        <SectionNav groups={groups} wide={false} />
      )}
    </Box>
  );
};

export default SettingsPage;
